import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import MeshGradient from '../../components/ui/MeshGradient';
import NoiseTexture from '../../components/ui/NoiseTexture';
import DownloadWindowsLink from '../../components/ui/DownloadWindowsLink';
import { clearJustSignedUp, markJustSignedUp, peekAfterLogin, peekInvitePreview } from '../../utils/afterLogin';
import './Auth.css';

const Register = () => {
  const navigate = useNavigate();
  const lockedEmail = peekInvitePreview()?.email || '';
  const [email, setEmail] = useState(lockedEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(lockedEmail ? 'password' : 'email'); // 'email', 'password', or 'exists'

  const handleEmailContinue = (e) => {
    e.preventDefault();
    if (!email) return;
    // Just proceed to password - we'll check existence on signup
    setStep('password');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const { clientId } = window.CONFIG.cognito;

      const response = await fetch(`https://cognito-idp.eu-west-2.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp',
        },
        body: JSON.stringify({
          ClientId: clientId,
          Username: email,
          Password: password,
          UserAttributes: [
            { Name: 'email', Value: email },
          ],
        }),
      });

      const data = await response.json();

      if (data.UserSub) {
        if (peekAfterLogin()) markJustSignedUp();
        navigate('/auth/verify', { state: { email } });
      } else if (data.__type?.includes('UsernameExistsException')) {
        clearJustSignedUp();
        setStep('exists');
      } else if (data.__type?.includes('InvalidPasswordException')) {
        setError('Password must include uppercase, lowercase, numbers, and special characters');
      } else if (data.__type?.includes('InvalidParameterException')) {
        setError(data.message || 'Invalid email or password format');
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    const { domain, clientId, redirectUri, scopes } = window.CONFIG.cognito;
    
    // Generate PKCE code verifier and challenge (same as regular login)
    const verifier = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem('code_verifier', verifier);
    
    // Create SHA-256 hash for code challenge
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const challenge = btoa(String.fromCharCode(...hashArray))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    
    const params = new URLSearchParams({
      identity_provider: 'Google',
      redirect_uri: redirectUri,
      response_type: 'code',
      client_id: clientId,
      scope: scopes.join(' '),
      code_challenge_method: 'S256',
      code_challenge: challenge,
    });
    
    // Google sign-in works for both login and signup - Cognito handles it
    if (peekAfterLogin()) markJustSignedUp();
    window.location.href = `https://${domain}/oauth2/authorize?${params.toString()}`;
  };

  // User already exists - show login prompt
  if (step === 'exists') {
    return (
      <div className="auth-container">
        <MeshGradient />
        <NoiseTexture />
        <div className="auth-card auth-card--wide">
          <div className="auth-header">
            <img 
              src={`${process.env.PUBLIC_URL}/images/fokusLogo.svg`} 
              alt="Fokus Logo" 
              className="auth-logo-image"
            />
            <h1 className="auth-title">You already have an account</h1>
          </div>

          <div className="auth-email-display">
            <span>{email}</span>
          </div>

          <Link to="/auth/login" className="auth-button auth-button--primary" style={{ marginTop: '1rem', textDecoration: 'none', textAlign: 'center' }}>
            Log in here
          </Link>

          <div className="auth-divider-line">
            <span>or</span>
          </div>

          {!lockedEmail && (
            <button 
              type="button" 
              className="auth-button auth-button--social"
              onClick={() => {
                setStep('email');
                setEmail('');
                setPassword('');
                setConfirmPassword('');
              }}
            >
              Try a different email
            </button>
          )}
        </div>
        <DownloadWindowsLink />
      </div>
    );
  }

  return (
    <div className="auth-container">
      <MeshGradient />
      <NoiseTexture />
      <div className="auth-card auth-card--wide">
        <div className="auth-header">
          <img 
            src={`${process.env.PUBLIC_URL}/images/fokusLogo.svg`} 
            alt="Fokus Logo" 
            className="auth-logo-image"
          />
          <h1 className="auth-title">Get started with Fokus</h1>
          <p className="auth-tagline">
            {lockedEmail
              ? `This invite is for ${lockedEmail}. That address becomes the account.`
              : peekAfterLogin()
                ? 'Create an account to join the brain'
                : "Project management that thinks—so you don't have to."}
          </p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleEmailContinue} className="auth-form">
            {error && <div className="auth-error">{error}</div>}
            
            <div className="auth-field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoFocus
              />
            </div>

            <button type="submit" className="auth-button auth-button--primary" disabled={loading}>
              Continue
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            {error && <div className="auth-error">{error}</div>}
            
            <div className="auth-email-display">
              <span>{email}</span>
              {!lockedEmail && (
                <button 
                  type="button" 
                  className="auth-link-button"
                  onClick={() => setStep('email')}
                >
                  Change
                </button>
              )}
            </div>

            <div className="auth-field">
              <label htmlFor="password">Create password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoFocus
              />
              <span className="auth-hint">Min 8 characters with uppercase, lowercase & numbers</span>
            </div>

            <div className="auth-field">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button type="submit" className="auth-button auth-button--primary" disabled={loading}>
              {loading ? 'Creating account...' : 'Sign up'}
            </button>
          </form>
        )}

        <div className="auth-divider-line">
          <span>or continue with</span>
        </div>

        <button 
          type="button" 
          className="auth-button auth-button--social"
          onClick={handleGoogleSignup}
        >
          <svg className="auth-social-icon" viewBox="0 0 24 24" width="18" height="18">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="auth-hint-text">
          Using a work email helps find teammates and boost collaboration.
        </p>

        <div className="auth-footer">
          <div className="auth-footer-links">
            <span>Already have an account?</span>
            <Link to="/auth/login" className="auth-link">Log in</Link>
          </div>
        </div>

        <p className="auth-legal">
          By proceeding, you agree to our <a href="/terms" className="auth-link">Terms of Service</a> and <a href="/privacy" className="auth-link">Privacy Policy</a>
        </p>
      </div>
      <DownloadWindowsLink />
    </div>
  );
};

export default Register;
